import { AxiosResponse } from "axios";
import { defineStore, storeToRefs } from "pinia";
import dayjs from "dayjs";
import { useItemsStore, useSessionStore, useSitesStore } from "@/stores";
import type IStock from "@/models/responses/IStock";
import Stock from "@/models/frontend/Stock";
import ISetStockRequest from "@/models/requests/ISetStockRequest";
import IIncreaseStockRequest from "@/models/requests/IIncreaseStockRequest";
import IDecreaseStockRequest from "@/models/requests/IDecreaseStockRequest";
import api from "@/services/api";
import IItemSignatureRaw from "@/models/responses/IItemSignatureRaw";

export const useStockStore = defineStore("stock", {
  state: () => ({
    error: "" as any,
    data: [] as Stock[],
  }),
  actions: {
    async fetchAll() {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      this.error = "";
      await api
        .get<IStock[]>(`v1.0/stocks?siteId=${selectedSiteId.value}`)
        .then((response) => (this.data = response.data.map((stock) => this.mapToFrontend(stock))))
        .catch((error) => (this.error = error));
    },
    async fetchAllByItem(itemId: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);

      let currentItemStock = <Stock[]>[];
      let requestError = "";
      await api
        .get<IStock[]>(`v1.0/stocks?siteId=${selectedSiteId.value}&itemId=${itemId}`)
        .then((response) => (currentItemStock = response.data.map((stock) => this.mapToFrontend(stock))))
        .catch((error) => (requestError = error));

      if (requestError) {
        console.error(requestError);
        return [];
      }
      
      return currentItemStock;
    },
    async fetchSignaturesForItem(itemId: number) {
      const sessionStore = useSessionStore();
      const { selectedSiteId } = storeToRefs(sessionStore);
      this.error = "";
      let signatures: IItemSignatureRaw[] = [];
      await api
        .get<IItemSignatureRaw[]>(`v1.0/stocks/signatures?siteId=${selectedSiteId.value}&itemId=${itemId}`)
        .then((response) => (signatures = response.data))
        .catch((error) => (this.error = error));
      return signatures;
    },
    async postStockSet(requestBody: ISetStockRequest) {
      this.error = "";
      await api
        .post<IStock, AxiosResponse<IStock, ISetStockRequest>, ISetStockRequest>(`v1.0/stocks/.set`, requestBody)
        .then((response) => {
          const stockResponse = response.data;
          if (response.data !== null) {
            const dataIndex = this.data.findIndex(
              (stock) => stock.id === stockResponse.id
            );
            if (dataIndex > -1) {
              this.data[dataIndex] = this.mapToFrontend(stockResponse);
            } else {
              this.data.push(this.mapToFrontend(stockResponse));
            }
          }
          else {
            const dataIndex = this.data.findIndex(
              (stock) =>
                stock.site.id === requestBody.siteId &&
                stock.item.id === requestBody.itemId &&
                (!requestBody.lot || stock.lot?.localeCompare(requestBody.lot) === 0) &&
                (!requestBody.bestBefore || stock.bestBeforeAsDate?.getTime() === new Date(requestBody.bestBefore).getTime()) &&
                (!requestBody.expiry || stock.expiryAsDate?.getTime() === new Date(requestBody.expiry).getTime())
            );
            if (dataIndex > -1 && dataIndex < this.data.length) {
              this.data.splice(dataIndex, 1);
            }
          }
        })
        .catch((error) => (this.error = error));
    },
    async postStockIncrease(requestBody: IIncreaseStockRequest) {
      this.error = "";
      await api
        .post<IStock, AxiosResponse<IStock, IIncreaseStockRequest>, IIncreaseStockRequest>(`v1.0/stocks/.increase`, requestBody)
        .then((response) => {
          const stockResponse = response.data;
          const dataIndex = this.data.findIndex(
            (stock) => stock.id === stockResponse.id
          );
          if (dataIndex > -1) {
            this.data[dataIndex] = this.mapToFrontend(stockResponse);
          } else {
            this.data.push(this.mapToFrontend(stockResponse));
          }
        })
        .catch((error) => (this.error = error));
    },
    async postStockDecrease(requestBody: IDecreaseStockRequest) {
      this.error = "";
      await api
        .post<IStock, AxiosResponse<IStock, IDecreaseStockRequest>, IDecreaseStockRequest>(`v1.0/stocks/.decrease`, requestBody)
        .then((response) => {
          const stockResponse = response.data;
          if (response.data !== null) {
            const dataIndex = this.data.findIndex(
              (stock) => stock.id === stockResponse.id
            );
            if (dataIndex > -1) {
              this.data[dataIndex] = this.mapToFrontend(stockResponse);
            } else {
              this.data.push(this.mapToFrontend(stockResponse));
            }
          }
          else {
            const dataIndex = this.data.findIndex(
              (stock) =>
                stock.site.id === requestBody.siteId &&
                stock.item.id === requestBody.itemId &&
                (!requestBody.lot || stock.lot?.localeCompare(requestBody.lot) === 0) &&
                (!requestBody.bestBefore || stock.bestBeforeAsDate?.getTime() === new Date(requestBody.bestBefore).getTime()) &&
                (!requestBody.expiry || stock.expiryAsDate?.getTime() === new Date(requestBody.expiry).getTime())
            );
            if (dataIndex > -1 && dataIndex < this.data.length) {
              this.data.splice(dataIndex, 1);
            }
          }
        })
        .catch((error) => (this.error = error));
    },
    async postStockDecreaseSpill(requestBody: IDecreaseStockRequest) {
      this.error = "";
      await api
        .post<IStock[], AxiosResponse<IStock[], IDecreaseStockRequest>, IDecreaseStockRequest>(`v1.0/stocks/.decrease-spill`, requestBody)
        .then((response) => {
          // remove from data where siteId, itemId match
          this.data = this.data.filter((stock => !(stock.site.id === requestBody.siteId && stock.item.id === requestBody.itemId)));
          // then re-add the remaining stocks to data (which is specific to the siteId and itemId)
          const remainingStocks = response.data;
          if (remainingStocks !== null) {
            this.data.push(...remainingStocks.map((stock) => this.mapToFrontend(stock)));
          }
        })
        .catch(error => {
          if (error.response) {
            // Server responded with an error status code (4xx or 5xx)
            const problem = error.response.data;
      
            if (problem.type === 'https://tools.ietf.org/html/rfc7231#section-6.5.1'
              || problem.type === 'https://tools.ietf.org/html/rfc9110#section-15.6.1'
            ) {
              // invalid operation error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.detail;
            } else {
              // unexpected error
              console.error(problem.title + ': ' + problem.detail);
              this.error = problem.title + ': ' + problem.detail;
            }
          } else if (error.request) {
            // no response received
            console.error('No response from server:', error.request);
            this.error = 'Server did not respond. Please try again later.';
          } else {
            // other errors
            console.error('Axios error:', error.message);
            this.error = 'Unexpected error: ' + error.message;
          }
        });
    },
    mapToFrontend(stock: IStock) {
      const sitesStore = useSitesStore();
      const itemsStore = useItemsStore();
      
      const site = sitesStore.getById(stock.siteId);
      const item = itemsStore.getById(stock.itemId);

      const m_bestBefore = dayjs(stock.bestBefore);
      const m_expiration = dayjs(stock.expiry);
      const now = dayjs();
      const bestBeforeExceeded = m_bestBefore.isBefore(now);
      const expirationExceeded = m_expiration.isBefore(now);
      const bestBeforeLeadTimeExceeded = m_bestBefore
        .subtract(item.bestBeforeLeadTime, "day")
        .isBefore(now);
      const expirationLeadTimeExceeded = m_expiration
        .subtract(item.expirationLeadTime, "day")
        .isBefore(now);
    
      return new Stock(
        stock.id,
        site,
        item,
        stock.quantity,
        stock.estimatedQuantity,
        stock.lot,
        stock.bestBefore,
        stock.expiry,
        optionalDate(stock.bestBefore),
        optionalDate(stock.expiry),
        bestBeforeExceeded,
        expirationExceeded,
        bestBeforeLeadTimeExceeded,
        expirationLeadTimeExceeded,
        undefined
      );
    },
  },
  getters: {
    getItemById: (state) => (id: number) =>
      state.data.find((stock) => stock.id === id),
    getItemsByItemId: (state) => (itemId: number) =>
      state.data.filter((stock) => stock.item.id === itemId),
    getItemStockQuantity: (state) => (itemId: number) =>
      state.data
        .filter((stock) => stock.item.id === itemId)
        .reduce((acc, stock) => acc + stock.quantity, 0),
  },
});

const optionalDate = (value: string | undefined): Date | undefined => {
  return value ? new Date(value) : undefined;
};
